import { appConfig } from '@/app/config/appConfig';
import { STAGE_THEME_COLORS } from '@/theme/stage-colors';
import { normalizeStageId } from '@/utils/stage/stage-utils';

const toPixiHex = (hex: string): number => Number.parseInt(hex.replace('#', '0x'), 16);

function parseHex(hex: string): { r: number; g: number; b: number } {
  const normalized = hex.replace('#', '');
  const value = Number.parseInt(normalized, 16);
  return {
    r: (value >> 16) & 0xff,
    g: (value >> 8) & 0xff,
    b: value & 0xff,
  };
}

function toHex(r: number, g: number, b: number): string {
  const clamp = (channel: number) => Math.max(0, Math.min(255, Math.round(channel)));
  return `#${[r, g, b].map((channel) => clamp(channel).toString(16).padStart(2, '0')).join('')}`;
}

export function mixHex(a: string, b: string, ratio: number): string {
  const from = parseHex(a);
  const to = parseHex(b);
  const t = Math.max(0, Math.min(1, ratio));
  return toHex(
    from.r + (to.r - from.r) * t,
    from.g + (to.g - from.g) * t,
    from.b + (to.b - from.b) * t,
  );
}

export function lightenHex(hex: string, amount: number): string {
  return mixHex(hex, '#ffffff', amount);
}

export function darkenHex(hex: string, amount: number): string {
  return mixHex(hex, '#000000', amount);
}

export function hexToRgba(hex: string, alpha: number): string {
  const { r, g, b } = parseHex(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Neutral dark fill for control buttons and full-viewport shell backdrop. */
const MAZE_CHROME_SURFACE_HEX = '#1c1b20' as const;

const STATIC_APP_COLOR = {
  background: '#1a1108',
  eclipse: '#b45309',
  eclipseSoft32: 'rgba(180, 83, 9, 0.32)',
  eclipseSoft72: 'rgba(180, 83, 9, 0.72)',
  panel: '#2a1d0d',
  panelBorder: '#3d2b14',
  accent: '#d4af37',
  accentRgb: '212, 175, 55',
  accentBright: '#fcd34d',
  action: '#b45309',
  actionHover: '#d97706',
  white: '#ffffff',
  black: '#000000',
  warning: '#f59e0b',
  danger: '#ef4444',
  dangerBg: '#450a0a',
  dangerBgHover: '#7f1d1d',
  dangerBorder: 'rgba(127, 29, 29, 0.5)',
  dangerText: '#fee2e2',
  overlay: 'rgba(26, 17, 8, 0.9)',
  panelSoft: 'rgba(42, 29, 13, 0.8)',
  accentSoft70: 'rgba(212, 175, 55, 0.7)',
  accentSoft60: 'rgba(212, 175, 55, 0.6)',
  accentSoft40: 'rgba(212, 175, 55, 0.4)',
  accentSoft30: 'rgba(212, 175, 55, 0.3)',
  accentSoft20: 'rgba(212, 175, 55, 0.2)',
  whiteSoft10: 'rgba(255, 255, 255, 0.1)',
  whiteSoft5: 'rgba(255, 255, 255, 0.05)',
  yellowGlow: 'rgba(250, 204, 21, 0.2)',
  info: '#38bdf8',
  infoBright: '#bae6fd',
  infoSoft70: 'rgba(56, 189, 248, 0.7)',
  infoSoft60: 'rgba(56, 189, 248, 0.6)',
  infoSoft40: 'rgba(56, 189, 248, 0.4)',
  infoSoft20: 'rgba(56, 189, 248, 0.2)',
  infoGlow: 'rgba(56, 189, 248, 0.24)',
  actionShadow: 'rgba(180, 83, 9, 0.6)',
  frameShadow: 'rgba(180, 83, 9, 0.5)',
  redGlow: 'rgba(239, 68, 68, 0.4)',
  controlSurface: MAZE_CHROME_SURFACE_HEX,
} as const;

function createDynamicAppColor() {
  const stageId = normalizeStageId(appConfig.stage.number);
  const stageTheme = STAGE_THEME_COLORS[stageId];
  const background = stageTheme.background;
  const accent = stageTheme.resultAccent;
  const { r: ar, g: ag, b: ab } = parseHex(accent);
  const accentBright = lightenHex(accent, 0.2);
  const panel = mixHex(background, stageTheme.eclipse, 0.42);
  const panelBorder = lightenHex(panel, 0.2);
  const action = mixHex(stageTheme.eclipse, accent, 0.38);
  const info = lightenHex(stageTheme.eclipse, 0.12);
  const infoBright = lightenHex(info, 0.45);

  return {
    background,
    eclipse: stageTheme.eclipse,
    eclipseSoft32: hexToRgba(stageTheme.eclipse, 0.32),
    eclipseSoft72: hexToRgba(stageTheme.eclipse, 0.72),
    panel,
    panelBorder,
    accent,
    accentRgb: `${ar}, ${ag}, ${ab}`,
    accentBright,
    action,
    actionHover: lightenHex(action, 0.08),
    white: '#ffffff',
    black: '#000000',
    warning: accentBright,
    danger: '#ef4444',
    dangerBg: '#450a0a',
    dangerBgHover: '#7f1d1d',
    dangerBorder: 'rgba(127, 29, 29, 0.5)',
    dangerText: '#fee2e2',
    overlay: hexToRgba(background, 0.9),
    panelSoft: hexToRgba(panel, 0.82),
    accentSoft70: hexToRgba(accent, 0.7),
    accentSoft60: hexToRgba(accent, 0.6),
    accentSoft40: hexToRgba(accent, 0.4),
    accentSoft30: hexToRgba(accent, 0.3),
    accentSoft20: hexToRgba(accent, 0.2),
    whiteSoft10: 'rgba(255, 255, 255, 0.1)',
    whiteSoft5: 'rgba(255, 255, 255, 0.05)',
    yellowGlow: hexToRgba(accentBright, 0.22),
    info,
    infoBright,
    infoSoft70: hexToRgba(info, 0.7),
    infoSoft60: hexToRgba(info, 0.6),
    infoSoft40: hexToRgba(info, 0.4),
    infoSoft20: hexToRgba(info, 0.2),
    infoGlow: hexToRgba(infoBright, 0.24),
    actionShadow: hexToRgba(darkenHex(action, 0.08), 0.6),
    frameShadow: hexToRgba(accent, 0.4),
    redGlow: 'rgba(239, 68, 68, 0.4)',
    controlSurface: MAZE_CHROME_SURFACE_HEX,
  } as const;
}

export const APP_COLOR =
  appConfig.stage.themeMode === 'DYNAMIC' ? createDynamicAppColor() : STATIC_APP_COLOR;

export const PIXI_COLOR = {
  floor: toPixiHex(APP_COLOR.background),
  wall: toPixiHex(APP_COLOR.warning),
  beaconOuter: toPixiHex(APP_COLOR.accent),
  beaconInner: toPixiHex(APP_COLOR.accentBright),
  tutorialOuter: toPixiHex(APP_COLOR.info),
  tutorialInner: toPixiHex(APP_COLOR.infoBright),
  trail: toPixiHex(APP_COLOR.accent),
  playerOuter: toPixiHex(APP_COLOR.accent),
  playerBase: toPixiHex(APP_COLOR.action),
  playerCore: toPixiHex(APP_COLOR.accentBright),
  playerSpecular: toPixiHex(APP_COLOR.white),
} as const;
